import { Component, Input, OnInit } from '@angular/core';
import { RouteTrackerState } from '@app/core/helper/route-tracker.helper';
import { IBreadcrumb } from '@app/models/beadcrumb/breadcrumb.model';
import { Observable, combineLatest, map } from 'rxjs';

/**
 * Tidies the route's breadcrumb trail for display (2026-11-04):
 *  - a crumb repeating the one before it (same label, or the same address) is dropped - parent and child
 *    routes of one section often carry the same label, which showed "Waiting List > Waiting List" beside
 *    a "Waiting List" title;
 *  - the current page (the last crumb) is left off - it only repeated the bold title and linked to the page
 *    already open - so a one-level section shows no trail at all (Ronan did this by hand for High-Risk
 *    Alerts, 9cdc5ed1) and every crumb shown is a working link to a level above.
 */
export function tidyBreadcrumbs(items: ReadonlyArray<IBreadcrumb> | null | undefined, recordNames: Readonly<Record<string, string>> = {}): ReadonlyArray<IBreadcrumb> {
  const nameOf = (b: IBreadcrumb) => (b.termKey ? `term:${b.termKey}${b.labelSuffix ?? ''}` : (b.label ?? '').trim().toLowerCase());
  const trail: IBreadcrumb[] = [];
  for (const item of items ?? []) {
    if (!item || !nameOf(item)) continue;
    const previous = trail[trail.length - 1];
    if (previous && nameOf(previous) === nameOf(item)) continue;
    // Same address as the crumb before (an empty child path): keep the more specific, later label.
    if (previous && previous.link && item.link && previous.link === item.link) trail.pop();
    trail.push(item);
  }
  // The current page is already the bold title beside the trail, so it is left off: the trail lists only the
  // levels above it, each a link back up ("Programmes / Programme" on an activity page).
  trail.pop();
  // A crumb for one record (its address ends in the record's id) shows that record's name once the page has it.
  return trail.map(b => {
    const id = (b.link ?? '').split('/').pop()?.toLowerCase();
    const name = id ? recordNames[id] : undefined;
    return name ? { ...b, label: name, termKey: undefined, labelSuffix: undefined } : b;
  });
}

@Component({
    selector: 'app-page-title',
    templateUrl: './pagetitle.component.html',
    styleUrls: ['./pagetitle.component.scss'],
    standalone: false
})
export class PagetitleComponent implements OnInit {

  @Input() title: string;
  @Input() backRouterLink: string | undefined = undefined;

  public breadcrumbItems$: Observable<ReadonlyArray<IBreadcrumb>> = combineLatest([
    RouteTrackerState.breadcrumbs$,
    RouteTrackerState.recordNames$,
  ]).pipe(map(([items, names]) => tidyBreadcrumbs(items, names)));

  constructor() { }

  ngOnInit() {
  }

}
